import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { FileText, FileCode, Copy, Check, Download, Maximize2, Minimize2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { getFileExtension } from '../../lib/fileTypes';
import Tip from '../ui/tip';

/**
 * The instrument strip above a text-only document.
 *
 * Device presets, zoom, reload and zen all simulate a *viewport*, which a
 * reflowing document doesn't have. What's left is the format it's stored in,
 * getting a copy of it, and how much of the row it occupies.
 *
 * Exported in three pieces because XML doesn't get its own bar: XmlViewer
 * already ships a toolbar with search, tree/raw and copy, so stacking a second
 * one above it would mean two glass strips, two copy buttons and two
 * Maximize2 icons meaning different things. Preview drops DocFormatControl and
 * DocActions into XmlViewer's existing bar instead, and only markdown gets the
 * assembled DocToolbar.
 */

// Module scope: none of this depends on props, and these components re-render
// behind every keystroke in the editor.
// `label` is the pill text, `name` the spoken form for tooltips and a11y —
// "Store this document as MD" reads like a typo.
const FORMATS = [
    { ext: 'md', label: 'MD', name: 'Markdown', Icon: FileText },
    { ext: 'xml', label: 'XML', name: 'XML', Icon: FileCode },
];

// The switcher renames the file, so it's only offered where a rename is
// meaningful. `.svg`/`.xsl`/`.plist` are XML too, but turning an icon into
// `icon.md` is vandalism, not a format switch — those get a static chip.
const SWITCHABLE_EXTENSIONS = new Set(['md', 'markdown', 'mdx', 'txt', 'xml']);

const MIME_TYPES = {
    md: 'text/markdown',
    markdown: 'text/markdown',
    mdx: 'text/markdown',
    xml: 'application/xml',
    svg: 'image/svg+xml',
};

const ICON_BUTTON =
    'flex flex-shrink-0 items-center justify-center rounded-lg p-2 text-muted-foreground ' +
    'transition-colors hover:bg-white/5 hover:text-foreground active:bg-white/10 ' +
    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:p-1.5';

const FormatSwitcher = memo(function FormatSwitcher({ activeExt, onChangeFormat }) {
    const activeIndex = Math.max(0, FORMATS.findIndex((f) => f.ext === activeExt));

    return (
        <div
            role="group"
            aria-label="Document format"
            className="relative flex flex-shrink-0 items-center rounded-lg border border-white/5 bg-white/5 p-0.5"
        >
            {/* The indicator is the motion; the labels just change colour under
                it. Track padding is 2px, so a button is (100% - 4px)/2 wide and
                translating by exactly 100% of that lands on the second button's
                left edge. */}
            <div
                aria-hidden
                className="absolute inset-y-0.5 left-0.5 w-[calc(50%-2px)] rounded-md bg-primary shadow-lg shadow-primary/20 transition-transform duration-300 ease-out motion-reduce:transition-none"
                style={{ transform: `translateX(${activeIndex * 100}%)` }}
            />
            {FORMATS.map(({ ext, label, name, Icon }) => {
                const isActive = ext === activeExt;
                return (
                    // The pill already shows "MD"/"XML", so the button keeps its
                    // own `aria-label` (the spoken name) and the tooltip carries
                    // the longer explanation as the description.
                    <Tip key={ext} label={`Store this document as ${name}`}>
                        <button
                            type="button"
                            // A lit pill is a statement, not a button. `.txt`
                            // lights Markdown, and clicking it must not silently
                            // rename the file to `.md`.
                            onClick={() => { if (!isActive) onChangeFormat(ext); }}
                            aria-pressed={isActive}
                            aria-label={name}
                            data-testid={`doc-format-${ext}`}
                            className={cn(
                                'relative z-10 flex flex-1 items-center justify-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                                isActive ? 'text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                            )}
                        >
                            <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                            {/* Short enough to survive a 380px panel, so no
                                longer hidden below `sm` — and now a format code
                                rather than a word, so it's set in mono. */}
                            <span className="font-mono tracking-wider">{label}</span>
                        </button>
                    </Tip>
                );
            })}
        </div>
    );
});

/**
 * The format switcher, or a read-only chip where switching would be wrong.
 */
export const DocFormatControl = memo(function DocFormatControl({ file, readOnly, onChangeFormat }) {
    const ext = getFileExtension(file?.name);
    const canSwitch = !readOnly && !!onChangeFormat && SWITCHABLE_EXTENSIONS.has(ext);

    if (!canSwitch) {
        return (
            <span className="flex-shrink-0 rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {ext || 'text'}
            </span>
        );
    }

    // `.txt`/`.mdx` render as markdown but aren't `.md`; point the switcher at
    // the pill they actually correspond to rather than leaving it unlit.
    return (
        <FormatSwitcher
            activeExt={ext === 'xml' ? 'xml' : 'md'}
            onChangeFormat={onChangeFormat}
        />
    );
});

/**
 * Copy / download / expand. `showCopy` is off where the host already has one.
 */
export const DocActions = memo(function DocActions({
    file,
    isExpanded,
    onToggleExpand,
    showCopy = true,
}) {
    const [copied, setCopied] = useState(false);
    const copyTimer = useRef(null);

    useEffect(() => () => clearTimeout(copyTimer.current), []);

    // file.content changes on every keystroke, so it's read inside the handler
    // rather than subscribed to as a prop.
    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(file?.content || '');
            setCopied(true);
            clearTimeout(copyTimer.current);
            copyTimer.current = setTimeout(() => setCopied(false), 1500);
            toast.success('Copied to clipboard');
        } catch {
            toast.error('Could not copy to clipboard');
        }
    }, [file]);

    const handleDownload = useCallback(() => {
        const blob = new Blob([file?.content || ''], {
            type: MIME_TYPES[getFileExtension(file?.name)] || 'text/plain',
        });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = file?.name || 'document.txt';
        anchor.click();
        URL.revokeObjectURL(url);
    }, [file]);

    return (
        <div className="flex flex-shrink-0 items-center gap-1">
            {showCopy ? (
                <Tip label="Copy document source">
                    <button
                        type="button"
                        onClick={handleCopy}
                        className={ICON_BUTTON}
                        data-testid="doc-copy"
                    >
                        {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                    </button>
                </Tip>
            ) : null}

            <Tip label={`Download ${file?.name || 'document'}`}>
                <button
                    type="button"
                    onClick={handleDownload}
                    className={ICON_BUTTON}
                    data-testid="doc-download"
                >
                    <Download className="h-4 w-4" />
                </button>
            </Tip>

            {/* Mobile has no sibling surface to expand into, so Workspace
                withholds the handler and the button disappears with it. */}
            {onToggleExpand ? (
                <>
                    <span aria-hidden className="mx-1 hidden h-4 w-px bg-white/10 md:block" />
                    <Tip
                        label={isExpanded ? 'Collapse panel' : 'Expand to fill workspace'}
                        hint={isExpanded ? 'Esc' : undefined}
                    >
                        <button
                            type="button"
                            onClick={onToggleExpand}
                            className={ICON_BUTTON}
                            data-testid="doc-expand"
                        >
                            {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                        </button>
                    </Tip>
                </>
            ) : null}
        </div>
    );
});

/**
 * The assembled bar. Markdown only — XML composes the pieces into XmlViewer's
 * own toolbar instead.
 */
const DocToolbar = memo(function DocToolbar({
    file,
    readOnly,
    onChangeFormat,
    isExpanded,
    onToggleExpand,
}) {
    return (
        <div className="flex flex-shrink-0 items-center gap-2 overflow-x-auto border-b border-white/10 bg-black/80 px-3 py-2 no-scrollbar backdrop-blur-xl">
            <span className="hidden flex-shrink-0 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground lg:inline">
                Format
            </span>

            <DocFormatControl file={file} readOnly={readOnly} onChangeFormat={onChangeFormat} />

            <div className="ml-auto">
                <DocActions file={file} isExpanded={isExpanded} onToggleExpand={onToggleExpand} />
            </div>
        </div>
    );
});

export default DocToolbar;
